import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { BrandSignature } from '@/presentation/components/index.js';
import type { AuthController } from '@/presentation/controllers/auth-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAuth } from '@/presentation/hooks/use-auth.js';
import { MicrosoftSignInButton } from '@/presentation/pages/auth/components/microsoft-sign-in-button.js';
import { ENTRA_START_PATH } from '@/presentation/pages/auth/utils/microsoft-login.js';
import { ROUTES } from '@/shared/constants/routes.js';

interface Props {
    controller: AuthController;
    notice?: ReactNode;
}

export function LoginPage({ controller, notice }: Props) {
    const { translate, useAuthStore } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isLoading, error } = useAuth(controller);
    const sessionExpired = useAuthStore((s) => s.sessionExpired);
    const acknowledgeSessionExpired = useAuthStore((s) => s.acknowledgeSessionExpired);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showSessionExpired] = useState(sessionExpired);
    const [microsoftEnabled, setMicrosoftEnabled] = useState(false);

    useEffect(() => {
        if (sessionExpired) {
            acknowledgeSessionExpired();
        }
    }, [sessionExpired, acknowledgeSessionExpired]);

    useEffect(() => {
        let active = true;
        controller.getEntraOptions().then((result) => {
            if (active && result.isRight()) {
                setMicrosoftEnabled(result.value.enabled);
            }
        });
        return () => {
            active = false;
        };
    }, [controller]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailError('');
        setPasswordError('');
        let valid = true;
        if (!email.trim()) {
            setEmailError(translate('auth.emailRequired'));
            valid = false;
        }
        if (!password) {
            setPasswordError(translate('auth.passwordRequired'));
            valid = false;
        }
        if (!valid) {
            return;
        }
        const success = await login(email.toLowerCase().trim(), password);
        if (success) {
            const from = (location.state as { from?: { pathname: string; search: string } } | null)?.from;
            navigate(from ? `${from.pathname}${from.search}` : ROUTES.DASHBOARD, { replace: true });
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                background: 'linear-gradient(135deg, #12122B 0%, #1a1340 50%, #12122B 100%)'
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
                <Box sx={{ mb: 4 }}>
                    <BrandSignature variant="centered" logoHeight={40} showSubtitle />
                </Box>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            {translate('auth.welcome')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {translate('auth.loginSubtitle')}
                        </Typography>
                        {showSessionExpired && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {translate('auth.sessionExpired')}
                            </Alert>
                        )}
                        {notice}
                        {microsoftEnabled ? <MicrosoftSignInButton label={translate('auth.microsoftSignIn')} href={ENTRA_START_PATH} /> : null}
                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label={translate('auth.email')}
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError('');
                                }}
                                error={!!emailError}
                                helperText={emailError}
                                autoComplete="email"
                                size="small"
                                fullWidth
                            />
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    label={translate('auth.password')}
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError('');
                                    }}
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    autoComplete="current-password"
                                    size="small"
                                    fullWidth
                                />
                                <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ position: 'absolute', right: 4, top: 4 }} tabIndex={-1} size="small">
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </IconButton>
                            </Box>
                            {error && (
                                <Typography color="error" variant="body2">
                                    {error}
                                </Typography>
                            )}
                            <Button type="submit" variant="contained" size="large" disabled={isLoading} sx={{ mt: 1, color: 'white' }}>
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : translate('auth.enterBtn')}
                            </Button>
                        </Box>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Link component={RouterLink} to={ROUTES.FORGOT_PASSWORD} variant="body2">
                                {translate('auth.forgotPassword')}
                            </Link>
                        </Box>
                    </CardContent>
                </Card>
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: 'rgba(255,255,255,0.35)',
                        fontSize: 9,
                        mt: 3,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                    }}
                >
                    {translate('brand.copyright', { year: new Date().getFullYear() })}
                </Typography>
            </Box>
        </Box>
    );
}
